'use client';

import * as React from 'react';
import { H2, P } from '@/components/ui/typography';
import { slugify } from '@/lib/shared/utils/slugify';
import { getServerSideURL } from '@/lib/shared/utils/getURL';
import { CookbookMetadata } from '@/types/cookbook';
import Tag from './Tag';
import Image from 'next/image';
import Link from 'next/link';

export const ArticleRow = ({
  article,
  selectedTag,
}: {
  article: CookbookMetadata;
  selectedTag?: string | null;
}) => {
  return (
    <div className="relative flex items-center rounded-lg gap-8 p-2 hover:bg-foreground/5">
      <Link
        href={`/cookbook/${slugify(article.title)}`}
        className="absolute inset-0 rounded-lg no-underline hover:no-underline"
        aria-label={article.title}
        data-testid={`cookbook-article-link-${slugify(article.title)}`}
      />

      {article.thumbnail && (
        <Image
          loading="lazy"
          width={128}
          height={128}
          className="rounded object-cover"
          // In production, public/ is stripped from the deployed image (see Dockerfile), so
          // next/image's optimizer can't read the file locally — it must fetch it remotely
          // instead. In dev, public/ is present, and the local path also avoids the
          // optimizer's SSRF check rejecting a src that resolves to a loopback IP.
          src={
            process.env.NODE_ENV === 'production'
              ? `${getServerSideURL()}${article.thumbnail}`
              : article.thumbnail
          }
          alt=""
        />
      )}

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <H2>{article.title}</H2>
          {article.subtitle && <P>{article.subtitle}</P>}
        </div>
        <div className="relative z-10 flex gap-2 flex-wrap">
          {article.tags?.map((tag) => (
            <Tag key={tag} tag={tag} current={selectedTag != null && selectedTag === tag} />
          ))}
        </div>
      </div>
    </div>
  );
};
